/**
 * Loan Calculator Component
 * Calculates monthly payments, total interest, and total repayment
 */
import React, { useState, useEffect, useCallback } from "react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "./ui/card";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Slider } from "./ui/slider";
import { Badge } from "./ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./ui/select";
import { Calculator, DollarSign, Clock, Percent, TrendingUp, RefreshCcw, Building2, Landmark, Smartphone, User, FileSpreadsheet } from "lucide-react";
import AmortizationSchedule from "./AmortizationSchedule";

// Default interest rates by loan type
const LOAN_RATES = {
  business: 12.5,
  wholesale: 14.0,
  device: 18.0,
  individual: 15.0,
};

const LOAN_TYPE_LABELS = {
  business: "Business Loan",
  wholesale: "Wholesale Finance",
  device: "Device Finance",
  individual: "Personal Loan",
};

// Preset loan configurations
const LOAN_PRESETS = [
  { type: "business", amount: 25000, term: 24, label: "Business Starter", icon: Building2, color: "bg-blue-100 text-blue-700 hover:bg-blue-200" },
  { type: "wholesale", amount: 100000, term: 36, label: "Wholesale Growth", icon: Landmark, color: "bg-purple-100 text-purple-700 hover:bg-purple-200" },
  { type: "device", amount: 5000, term: 12, label: "Device Finance", icon: Smartphone, color: "bg-amber-100 text-amber-700 hover:bg-amber-200" },
  { type: "individual", amount: 10000, term: 18, label: "Personal Loan", icon: User, color: "bg-green-100 text-green-700 hover:bg-green-200" },
];

export const LoanCalculator = ({ 
  defaultAmount = 10000, 
  defaultTerm = 12, 
  loanType = "business",
  onApply,
  compact = false 
}) => {
  const [amount, setAmount] = useState(defaultAmount);
  const [term, setTerm] = useState(defaultTerm);
  const [interestRate, setInterestRate] = useState(LOAN_RATES[loanType] || 12.5);
  const [selectedLoanType, setSelectedLoanType] = useState(loanType);
  const [results, setResults] = useState(null);
  const [showSchedule, setShowSchedule] = useState(false);

  // Apply preset configuration
  const applyPreset = (preset) => {
    setAmount(preset.amount);
    setTerm(preset.term);
    setSelectedLoanType(preset.type);
    setInterestRate(LOAN_RATES[preset.type]);
  };

  // Calculate loan details
  const calculateLoan = useCallback(() => {
    if (!amount || !term || !interestRate) return;

    const principal = parseFloat(amount);
    const monthlyRate = parseFloat(interestRate) / 100 / 12;
    const numPayments = parseInt(term);

    // Monthly payment formula: M = P * [r(1+r)^n] / [(1+r)^n - 1]
    let monthlyPayment;
    if (monthlyRate === 0) {
      monthlyPayment = principal / numPayments;
    } else {
      monthlyPayment = 
        (principal * monthlyRate * Math.pow(1 + monthlyRate, numPayments)) /
        (Math.pow(1 + monthlyRate, numPayments) - 1);
    }

    const totalPayment = monthlyPayment * numPayments;
    const totalInterest = totalPayment - principal;

    setResults({
      monthlyPayment,
      totalPayment,
      totalInterest,
      principal,
      effectiveRate: (totalInterest / principal) * 100,
    });
  }, [amount, term, interestRate]);

  // Recalculate when inputs change
  useEffect(() => {
    calculateLoan();
  }, [calculateLoan]);

  // Update interest rate when loan type changes
  useEffect(() => {
    setInterestRate(LOAN_RATES[selectedLoanType] || 12.5);
  }, [selectedLoanType]);

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value);
  };

  const formatCurrencyPrecise = (value) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(value);
  };

  return (
    <Card className={`${compact ? 'shadow-sm' : 'shadow-lg'} border-0`} data-testid="loan-calculator">
      <CardHeader className={compact ? 'pb-2' : 'pb-4'}>
        <CardTitle className="flex items-center gap-2 text-xl">
          <Calculator className="h-5 w-5 text-blue-600" />
          Loan Calculator
        </CardTitle>
        {!compact && (
          <CardDescription>
            Estimate your monthly payments and total cost
          </CardDescription>
        )}
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Quick Preset Buttons */}
        <div className="space-y-2">
          <Label className="text-xs text-gray-500 uppercase tracking-wide">Quick Presets</Label>
          <div className="grid grid-cols-2 gap-2">
            {LOAN_PRESETS.map((preset) => {
              const Icon = preset.icon;
              return (
                <Button
                  key={preset.type}
                  variant="outline"
                  size="sm"
                  onClick={() => applyPreset(preset)}
                  className={`flex items-center justify-start gap-2 ${preset.color} border-0`}
                  data-testid={`preset-${preset.type}`}
                >
                  <Icon className="h-4 w-4" />
                  <span className="text-xs font-medium">{preset.label}</span>
                </Button>
              );
            })}
          </div>
        </div>

        {/* Loan Type Selector */}
        <div className="space-y-2">
          <Label className="flex items-center gap-2">
            Loan Type
          </Label>
          <Select
            value={selectedLoanType}
            onValueChange={setSelectedLoanType}
          >
            <SelectTrigger data-testid="calc-loan-type">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {Object.entries(LOAN_TYPE_LABELS).map(([value, label]) => (
                <SelectItem key={value} value={value}>
                  {label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Loan Amount */}
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <Label className="flex items-center gap-2">
              <DollarSign className="h-4 w-4 text-gray-500" />
              Loan Amount
            </Label>
            <span className="text-lg font-semibold text-blue-600">
              {formatCurrency(amount)}
            </span>
          </div>
          <Slider
            value={[amount]}
            onValueChange={(val) => setAmount(val[0])}
            min={1000}
            max={500000}
            step={1000}
            className="py-2"
            data-testid="calc-amount-slider"
          />
          <div className="flex justify-between text-xs text-gray-500">
            <span>$1,000</span>
            <span>$500,000</span>
          </div>
          <Input
            type="number"
            value={amount}
            onChange={(e) => setAmount(Math.max(1000, parseInt(e.target.value) || 1000))}
            className="mt-2"
            data-testid="calc-amount-input"
          />
        </div>

        {/* Loan Term */}
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <Label className="flex items-center gap-2">
              <Clock className="h-4 w-4 text-gray-500" />
              Loan Term
            </Label>
            <span className="text-lg font-semibold text-blue-600">
              {term} months
            </span>
          </div>
          <Slider
            value={[term]}
            onValueChange={(val) => setTerm(val[0])}
            min={3}
            max={60}
            step={3}
            className="py-2"
            data-testid="calc-term-slider"
          />
          <div className="flex justify-between text-xs text-gray-500">
            <span>3 months</span>
            <span>60 months</span>
          </div>
        </div>

        {/* Interest Rate */}
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <Label className="flex items-center gap-2">
              <Percent className="h-4 w-4 text-gray-500" />
              Interest Rate (Annual)
            </Label>
            <span className="text-lg font-semibold text-blue-600">
              {interestRate.toFixed(1)}%
            </span>
          </div>
          <Slider
            value={[interestRate]}
            onValueChange={(val) => setInterestRate(val[0])}
            min={5}
            max={30}
            step={0.5}
            className="py-2"
            data-testid="calc-rate-slider"
          />
          <div className="flex justify-between text-xs text-gray-500">
            <span>5%</span>
            <span>30%</span>
          </div>
        </div>

        {/* Results */}
        {results && (
          <div className="space-y-4 pt-4 border-t border-gray-200">
            <div className="grid grid-cols-1 gap-4">
              {/* Monthly Payment - Highlighted */}
              <div className="bg-gradient-to-r from-blue-600 to-blue-700 rounded-xl p-4 text-white">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-blue-100 text-sm">Monthly Payment</p>
                    <p className="text-3xl font-bold" data-testid="calc-monthly-payment">
                      {formatCurrencyPrecise(results.monthlyPayment)}
                    </p>
                  </div>
                  <TrendingUp className="h-8 w-8 text-blue-200" />
                </div>
              </div>

              {/* Other Results */}
              <div className="grid grid-cols-2 gap-3">
                <div className="bg-gray-50 rounded-lg p-3">
                  <p className="text-gray-500 text-xs uppercase tracking-wide">Total Interest</p>
                  <p className="text-lg font-semibold text-orange-600" data-testid="calc-total-interest">
                    {formatCurrency(results.totalInterest)}
                  </p>
                </div>
                <div className="bg-gray-50 rounded-lg p-3">
                  <p className="text-gray-500 text-xs uppercase tracking-wide">Total Repayment</p>
                  <p className="text-lg font-semibold text-gray-800" data-testid="calc-total-payment">
                    {formatCurrency(results.totalPayment)}
                  </p>
                </div>
              </div>
            </div>

            {/* Effective Rate Note */}
            <p className="text-xs text-gray-500 text-center">
              Effective interest rate: {results.effectiveRate.toFixed(1)}% of principal
            </p>
          </div>
        )}

        {/* View Amortization Schedule Button */}
        {results && (
          <Button
            variant="outline"
            className="w-full flex items-center gap-2"
            onClick={() => setShowSchedule(!showSchedule)}
            data-testid="view-schedule-btn"
          >
            <FileSpreadsheet className="h-4 w-4" />
            {showSchedule ? "Hide Payment Schedule" : "View Payment Schedule"}
          </Button>
        )}

        {/* Apply Now Button */}
        {onApply && (
          <Button 
            className="w-full bg-blue-600 hover:bg-blue-700"
            onClick={() => onApply({ amount, term, interestRate, loanType: selectedLoanType })}
            data-testid="calc-apply-btn"
          >
            Apply for This Loan
          </Button>
        )}

        {/* Reset Button */}
        <Button
          variant="ghost"
          className="w-full text-gray-500"
          onClick={() => {
            setAmount(defaultAmount);
            setTerm(defaultTerm);
            setSelectedLoanType(loanType);
            setInterestRate(LOAN_RATES[loanType]);
            setShowSchedule(false);
          }}
        >
          <RefreshCcw className="h-4 w-4 mr-2" />
          Reset Calculator
        </Button>

        {/* Amortization Schedule */}
        {showSchedule && results && (
          <div className="mt-4">
            <AmortizationSchedule
              principal={amount}
              annualRate={interestRate}
              termMonths={term}
            />
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default LoanCalculator;
